/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  ADD_FAMILY_INFORMATION_FACADE_TOKEN,
  IAddFamilyInformationFacade,
} from '../../../../../../data-access/src/lib/domain/facades-models/add-family-information-facade-interface';
import { PropertiesListItemsVM } from '../../../../../../../shared/ui/src/lib/models/sysque-properties-list-vm';
import { SysqueSwitchButtonVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-switch-button-vm';
import { SysqueDropdownConnectorsVM } from '../../../../../../../shared/ui/src/lib/models/sysque-dropdown-connectors-vm';
import {
  SysqueSizeRangeItemsVM,
  SysqueSizeRangeVM,
} from '../../../../../../../shared/ui/src/lib/models/sysque-size-range-vm';
import {
  AddFamilyInformationData,
  AddFamilyInformationReturnCodes,
  FamilyInformationItem,
} from '../../../models/family-information-model';
import { GlobalConstants } from '../../../constants/global-constants';
import { HandAndMachineBendSizeRangeVM, IFamilyInformationItem } from '../add-family-information-types';
import { AddFamilyProjectDataInfo } from '../../../../../../utils/src/lib/common-helper/add-family-project-data-info';
import { AlertStatus, IAlert } from '../../../../../../../shared/ui/src/lib/components/alert/alert-types';

@Component({
  selector: 'sysque-hand-and-machine-bend-family-information',
  templateUrl: './hand-and-machine-bend-family-information.component.html',
  styleUrl: './hand-and-machine-bend-family-information.component.scss',
  standalone: false
})
export class HandAndMachineBendFamilyInformation implements OnInit, OnDestroy {
  public alertStatus = AlertStatus.Warning;
  public warningMessage = '';
  public title = '';
  public image = '';
  public propertiesListItems: PropertiesListItemsVM[] = [];
  public areConnectorsVisible: boolean = false;
  public connectorsTitle: string;
  public connectorsItems: SysqueDropdownConnectorsVM[] = [];
  public fileName = '';
  public isSupportDisciplineVisible = false;
  public instanceDescriptionLabel = '';
  public instanceDescription = '';
  public typeDescriptionLabel = '';
  public typeDescription = '';
  public projectDataButtonInfo: SysqueSwitchButtonVM;
  public dataGrid: SysqueSizeRangeVM;
  public tabImage = '';
  public diagram = '';
  public item: IFamilyInformationItem;
  private alertHeight = 815;
  private withoutAlertHeight = 765;
  public isProjectDataBtnDisabled = false;
  public isAlertVisible = false;

  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: true,
    };
  }

  constructor(
    @Inject(ADD_FAMILY_INFORMATION_FACADE_TOKEN) private addFamilyInformationFacade: IAddFamilyInformationFacade
  ) {}

  public ngOnInit(): void {
    window.addEventListener('storage', this.handleStorageEvent.bind(this));
    this.addFamilyInformationFacade.registerControllers();
    const response = this.addFamilyInformationFacade.getFamilyInformationResponse();
    if (response.Data.IsFromAdd) {
      this.item = this.addFamilyInformationFacade.setItemToAdd(response.Data);
    } else {
      this.item = this.addFamilyInformationFacade.getFamilyInformationItem();
    }
    this.addFamilyInformationFacade.updateLocalStorage(GlobalConstants.PROJECT_PARAMS, []);
    this.setFamilyInformationData(response.Data, this.item);
    this.handleMessages(response);
    this.initData();
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.handleStorageEvent.bind(this));
  }

  //#region data initialization
  private initData(): void {
    this.titleInit();
    this.imageInit();
    this.propertiesInit();
    this.connectorsInit();
    this.instanceDescriptionInit();
    this.typeDescriptionInit();
    this.projectDataInit();
    this.tabImageInit();
    this.sizeRangeInit();
  }

  private titleInit(): void {
    if (this.item.isFromDuplicate || (this.item.itemType === 'ElectricalTypeItems' && this.item.isFromAdd)) {
      this.title = GlobalConstants.NEW_CONDUIT_TITLE;
    } else {
      this.title = this.item.name;
    }
  }

  private imageInit(): void {
    this.image = 'data:image/png;base64,' + this.item.image;
  }

  private propertiesInit(): void {
    this.propertiesListItems = [
      {
        label:
          this.item.isFromDuplicate || (this.item.itemType === 'ElectricalTypeItems' && this.item.isFromAdd)
            ? GlobalConstants.CONDUIT_NAME_LABEL
            : GlobalConstants.ITEM_TYPE_LABEL,
        value: this.item.isFromDuplicate
          ? ''
          : this.item.itemType === 'ElectricalTypeItems'
            ? this.item.name
            : this.item.fittingType,
        isEditable: this.item.itemType === 'ElectricalTypeItems' && !this.item.isFromEdit ? true: false,
      },
      {
        label: GlobalConstants.MANUFACTURER_LABEL,
        value: this.item.manufacturer,
        isEditable: false,
      },
    ];

    if (this.isElectricalTypeItems) {
      this.propertiesListItems.push({
        label: GlobalConstants.MATERIAL_LABEL,
        value: this.item.material,
        isEditable: !this.item.isFromEdit && !this.item.isFromAdd,
      });
    }

    this.propertiesListItems.push({
      label: GlobalConstants.ID_LABEL,
      value: this.item.id,
      isEditable: false,
    });

    if (this.item.isEquipment) {
      this.propertiesListItems.push({
        label: GlobalConstants.ADDED_WIRE_LENGTH_LABEL,
        value: this.item.addedWireLength,
        isEditable: !this.item.isFromEdit && !this.item.isFromAdd,
      });
    }
  }

  public connectorsInit(): void {
    if (this.item.connectorCount === 0) {
      return;
    }
    const itemConnectors = this.item.connectors;
    let selectedConnectors = [];
    if (itemConnectors) {
      selectedConnectors = itemConnectors?.split(' x ').map((x) => x.trim());
    }
    this.connectorsTitle = GlobalConstants.CONNECTORS_LABEL;
    const connectors = this.addFamilyInformationFacade.getConduitConnectors();
    const connectorsCount = this.item.connectorCount;
    this.areConnectorsVisible = connectorsCount > 0;
    for (let i = 0; i < connectorsCount; i++) {
      this.connectorsItems.push({
        label: `C${i + 1}`,
        data: connectors.map((x) => x.connector),
        selectedValue: Array.isArray(selectedConnectors)
          ? connectors?.find((x) => x.connector === selectedConnectors[i])?.connector
          : connectors?.find((x) => x.connector === selectedConnectors)?.connector,
      });
    }
  }

  private instanceDescriptionInit(): void {
    this.instanceDescriptionLabel = GlobalConstants.INSTANCE_DESCRIPTION_LABEL;
    this.instanceDescription = this.item.descriptionInstance;
  }

  private typeDescriptionInit(): void {
    this.typeDescriptionLabel = GlobalConstants.TYPE_DESCRIPTION_LABEL;
    this.typeDescription = this.item.descriptionType;
  }

  private projectDataInit(): void {
    this.projectDataButtonInfo = {
      title: GlobalConstants.PROJECT_DATA_LABEL,
      enabled: Array.isArray(this.item.parameters) && this.item.parameters.length > 0,
      labelForEnabled: GlobalConstants.ENABLED,
      labelForDisabled: GlobalConstants.DISABLED,
      hasOptionButton: true,
    };
  }

  private sizeRangeInit(): void {
    this.dataGrid = new SysqueSizeRangeVM();
    this.dataGrid.title = GlobalConstants.SIZE_RANGE_LABEL;
    const itemSizeRange: HandAndMachineBendSizeRangeVM = new HandAndMachineBendSizeRangeVM(
      this.item.sizeRange,
      this.item.bendRadius,
      this.item.leadLength,
      this.item.leadOutLength,
      this.item.takeUp
    );
    this.dataGrid.sizeRangeItems = this.addFamilyInformationFacade.convertHandAndMachineBendStringToSysqueSizeRangeVM(
      itemSizeRange,
      this.item.offsetType
    );
  }

  private tabImageInit(): void {
    this.tabImage = 'assets/img/' + this.item.offsetType.replace(/\s+/g, '') + '.png';
  }

  private handleMessages(response: FamilyInformationItem): void {
    if (
      response.ReturnCode == AddFamilyInformationReturnCodes.Success ||
      response.ReturnCode == AddFamilyInformationReturnCodes.NoMessage
    ) {
      this.hideAlert();
    } else if (response.ReturnCode == AddFamilyInformationReturnCodes.InvalidFamilies) {
      this.showAlert(GlobalConstants.PLEASE_UPDATE_FAMILIES_AND_TRY_AGAIN, AlertStatus.Warning);
    }
  }

  //#endregion

  public onPropertiesValueChanged($event: { value: string; label: string }): void {
    switch ($event.label) {
      case GlobalConstants.CONDUIT_NAME_LABEL:
        this.item.name = $event.value;
        break;
      case GlobalConstants.ITEM_TYPE_LABEL:
        this.item.partType = $event.value;
        break;
      case GlobalConstants.MANUFACTURER_LABEL:
        this.item.manufacturer = $event.value.toString();
        break;
      case GlobalConstants.ID_LABEL:
        this.item.id = $event.value.toString();
        break;
    }
  }

  public onConnectorValueChanged($event: { value: string; label: string }): void {
    const index = Number($event.label.replace('C', '')) - 1;
    let selectedConnectors;
    if(this.item.connectors === null) {
      selectedConnectors = [];
    } else {
      selectedConnectors = this.item.connectors.split(' x ').map((x) => x.trim());
    }
    selectedConnectors[index] = $event.value;
    this.item.connectors = selectedConnectors.join(' x ');
  }

  public onInstanceDescriptionValueChanged($event: string): void {
    this.item.descriptionInstance = $event;
  }

  public onTypeDescriptionValueChanged($event: string): void {
    this.item.descriptionType = $event;
  }

  public onSizeRangeSelectedItems($event: SysqueSizeRangeItemsVM[]): void {
    this.dataGrid.sizeRangeItems.forEach((element) => {
      element.isChecked = $event.some((x) => x.id === element.id) ? 1 : 0;
    });
    this.handleDataGridCellChanges();
  }

  public onCellValueChanged(): void {
    this.handleDataGridCellChanges();
  }

  public handleDataGridCellChanges(): void {
    const fieldFabSizeRange = this.addFamilyInformationFacade.convertSysqueSizeRangeVMToHandAndMachineBendString(
      this.dataGrid.sizeRangeItems,
      this.item.offsetType
    );
    this.item.sizeRange = fieldFabSizeRange?.sizeRange;
    this.item.bendRadius = fieldFabSizeRange?.bendRadius;
    this.item.leadLength = fieldFabSizeRange?.leadLength;
    this.item.leadOutLength = fieldFabSizeRange?.leadOutLength;
    this.item.takeUp = fieldFabSizeRange?.takeUp;
  }

  public doneClick(): void {
    new AddFamilyProjectDataInfo(this.addFamilyInformationFacade).getModifiedProjectParameters(
      this.item,
      this.isProjectDataBtnDisabled
    );
    this.addFamilyInformationFacade.saveFamilyInformationInXml(this.item);
  }

  public handleStorageEvent(event: StorageEvent): void {
    if (event.key === 'familyInformationSaveResponse') {
      if (event.newValue === '') {
        return;
      }
      const response = JSON.parse(event.newValue);
      if (response == AddFamilyInformationReturnCodes.Success) {
        this.addFamilyInformationFacade.updateItem(this.item);
        this.addFamilyInformationFacade.sendClosePopupAction();
      } else if (response == AddFamilyInformationReturnCodes.InvalidFamilies) {
        this.showAlert(GlobalConstants.PLEASE_UPDATE_FAMILIES_AND_TRY_AGAIN, AlertStatus.Warning);
      }
    }
  }

  /**
   * Handles the click event of the project data button.
   */
  public onProjectDataButtonClicked(): void {
    this.addFamilyInformationFacade.onClickedToOpenProjectData();
  }

  /**
   * Handles the click event of the project data switch button.
   * @param button - The SysqueSwitchButtonVM representing the clicked button.
   */
  public onProjectDataSwitchClicked(button: SysqueSwitchButtonVM): void {
    this.isProjectDataBtnDisabled = !button.enabled;
  }

  //#region alert
  private showAlert(message: string, alertStatus: AlertStatus): void {
    this.addFamilyInformationFacade.windowResize(this.alertHeight);
    this.warningMessage = message;
    this.alertStatus = alertStatus;
    this.isAlertVisible = true;
  }

  public hideAlert(): void {
    this.addFamilyInformationFacade.windowResize(this.withoutAlertHeight);
    this.warningMessage = '';
    this.isAlertVisible = false;
  }

  //#endregion

  private get isElectricalTypeItems(): boolean {
    return this.item.itemType === GlobalConstants.ELECTRICAL_TYPE_ITEMS;
  }

  private get isAddDefaultItem(): boolean {
    return this.item.itemType === GlobalConstants.ADD_DEFAULT_ITEM;
  }

  private setFamilyInformationData(data: AddFamilyInformationData, item: IFamilyInformationItem): void {
    item.serviceTypes = data.ServiceTypes;
    item.electricalType = data.ElectricalType;
    item.isClearence = data.IsClearance;
    item.partType = data.PartType;
    item.angle = data.Angle ?? item.angle;
    item.isWall = data.IsWall;
    item.bendType = data.BendType;
    item.connectorCount = data.ConnectorCount;
    item.serviceTypeColor = data.ServiceTypeColor;
    item.length = data.Length ?? item.length;
    item.csvName = data.CsvName;
    item.centerRadius = data.CenterRadius;
    item.diameter = data.Diameter;
    item.height = data.Height;
    item.width = data.Width;
    item.material = data.Material ?? item.material;
    item.panelCircuitChecked = data.PanelCircuitChecked;
    if (this.isAddDefaultItem) {
      item.isEquipment = data.IsEquipment;
      item.offsetType = data.OffsetType;
      item.fittingType = data.FittingType;
    }
    item.familyCategoryName = data.FamilyCategoryName;
    this.addFamilyInformationFacade.updateLocalStorage(GlobalConstants.FAMILY_INFORMATION_ITEM, item);
  }
}
